import React, { useEffect } from 'react';
import { useAccessibility } from '../contexts/AccessibilityContext';
import './PrivacyPolicy.css';
import { useFocusableNavigation } from '../hooks/useFocusableNavigation';

const PrivacyPolicy = () => {
  const { announce, focusFirstHeading } = useAccessibility();
  useFocusableNavigation();

  useEffect(() => {
    focusFirstHeading();
    announce('Privacy Policy page loaded');
    // Privacy Policy page loaded
  }, [announce, focusFirstHeading]);

  const handleCookieSettingsClick = () => {
    // User clicked to manage cookie settings
    // Clear existing consent to show banner again
    localStorage.removeItem('cookieConsent');
    announce('Cookie settings cleared. Please refresh the page to see the cookie banner.');
    // Refresh page to show cookie banner
    window.location.reload();
  };

  return (
    <div className="privacy-policy-page">
      <div className="container">
        <header className="page-header">
          <h1>Privacy & Cookie Policy</h1>
          <p className="page-description">
            How we collect, use, and protect your information
          </p>
        </header>

        <div className="policy-content">
          <section className="policy-section" aria-labelledby="overview-title">
            <h2 id="overview-title">Overview</h2>
            <p>
              Communication Matters Conference is committed to protecting your privacy and ensuring 
              transparency about how we collect and use your information. This policy explains our 
              practices regarding data collection, cookies, and your rights.
            </p>
            <p className="last-updated">
              <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </p>
          </section>

          <section className="policy-section" aria-labelledby="data-collection-title">
            <h2 id="data-collection-title">Information We Collect</h2>
            
            <h3>Personal Information</h3>
            <ul>
              <li><strong>Registration Data:</strong> Name, email address, organization, and professional details when you register for the conference</li>
              <li><strong>Session Preferences:</strong> Your bookmarked sessions and agenda preferences</li>
              <li><strong>Q&A Submissions:</strong> Questions you submit for conference sessions</li>
              <li><strong>Comments:</strong> Any comments you post on session pages</li>
            </ul>

            <h3>Automatically Collected Information</h3>
            <ul>
              <li><strong>Usage Analytics:</strong> How you interact with our website, pages visited, time spent</li>
              <li><strong>Technical Data:</strong> IP address, browser type, device information, operating system</li>
              <li><strong>Performance Data:</strong> Page load times, errors, and website performance metrics</li>
            </ul>
          </section>

          <section className="policy-section" aria-labelledby="cookies-title">
            <h2 id="cookies-title">Cookie Policy</h2>
            
            <p>
              We use cookies and similar technologies to enhance your experience on our website. 
              You can control cookie preferences through our cookie banner or the settings below.
            </p>

            <div className="cookie-types">
              <div className="cookie-type">
                <h3>🔧 Necessary Cookies</h3>
                <p><strong>Purpose:</strong> Essential for website functionality</p>
                <p><strong>Examples:</strong> Session management, security, accessibility preferences</p>
                <p><strong>Can be disabled:</strong> No - required for basic website operation</p>
              </div>

              <div className="cookie-type">
                <h3>📊 Analytics Cookies</h3>
                <p><strong>Purpose:</strong> Help us understand how visitors use our website</p>
                <p><strong>Examples:</strong> Google Analytics, page view tracking, user interaction analytics</p>
                <p><strong>Can be disabled:</strong> Yes - you can opt out without affecting functionality</p>
              </div>

              <div className="cookie-type">
                <h3>📢 Marketing Cookies</h3>
                <p><strong>Purpose:</strong> Track visitors across websites for advertising purposes</p>
                <p><strong>Examples:</strong> Social media pixels, advertising network cookies</p>
                <p><strong>Can be disabled:</strong> Yes - you can opt out without affecting functionality</p>
              </div>
            </div>

            <div className="cookie-management">
              <h3>Manage Your Cookie Preferences</h3>
              <p>
                You can change your cookie preferences at any time by clicking the button below. 
                This will clear your current settings and show the cookie banner again.
              </p>
              <button 
                onClick={handleCookieSettingsClick}
                className="btn btn-primary"
                aria-label="Open cookie settings to manage preferences"
              >
                🍪 Manage Cookie Settings
              </button>
            </div>
          </section>

          <section className="policy-section" aria-labelledby="data-use-title">
            <h2 id="data-use-title">How We Use Your Information</h2>
            <ul>
              <li><strong>Conference Management:</strong> Process registrations, manage your agenda, send updates</li>
              <li><strong>Communication:</strong> Send important conference information, announcements, and updates</li>
              <li><strong>Improvement:</strong> Analyze website usage to improve user experience and content</li>
              <li><strong>Support:</strong> Respond to your questions and provide technical support</li>
              <li><strong>Legal Compliance:</strong> Meet legal obligations and protect our rights</li>
            </ul>
          </section>

          <section className="policy-section" aria-labelledby="data-storage-title">
            <h2 id="data-storage-title">Data Storage</h2>

            <h3>Your Account Data</h3>
            <p>
              Your personal agenda (bookmarked sessions) and account preferences are saved
              securely to your account on our servers, not just to your browser. This means:
            </p>
            <ul>
              <li>Your agenda syncs automatically across any device or browser once you're logged in</li>
              <li>It isn't lost if you clear your browser data or cookies</li>
              <li>You can pick up where you left off by logging in with the same account elsewhere</li>
            </ul>
            <p>
              Some settings that are purely about how the app looks on a particular device —
              such as high-contrast mode or text size — are kept locally on that device and
              aren't part of your account.
            </p>

            <h3>Server-Side Data</h3>
            <p>
              The following is stored securely on our servers:
            </p>
            <ul>
              <li>Registration information and user accounts</li>
              <li>Your bookmarked sessions and personal agenda</li>
              <li>Q&A submissions and comments</li>
              <li>Analytics data (anonymized)</li>
              <li>Session and conference content</li>
            </ul>
          </section>

          <section className="policy-section" aria-labelledby="rights-title">
            <h2 id="rights-title">Your Rights</h2>
            <p>You have the right to:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
              <li><strong>Erasure:</strong> Request deletion of your personal data</li>
              <li><strong>Portability:</strong> Receive your data in a portable format</li>
              <li><strong>Objection:</strong> Object to processing of your personal data</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing at any time</li>
            </ul>
          </section>

          <section className="policy-section" aria-labelledby="third-parties-title">
            <h2 id="third-parties-title">Third-Party Services</h2>
            <p>We use the following third-party services:</p>
            <ul>
              <li><strong>Supabase:</strong> Database and authentication services</li>
              <li><strong>Google Analytics:</strong> Website analytics (if you consent to analytics cookies)</li>
              <li><strong>Vercel:</strong> Website hosting and content delivery</li>
            </ul>
            <p>
              These services have their own privacy policies and may collect data independently. 
              We recommend reviewing their privacy policies for more information.
            </p>
          </section>

          <section className="policy-section" aria-labelledby="security-title">
            <h2 id="security-title">Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal data:
            </p>
            <ul>
              <li>Encrypted data transmission (HTTPS)</li>
              <li>Secure database storage with access controls</li>
              <li>Regular security updates and monitoring</li>
              <li>Limited access to personal data on a need-to-know basis</li>
            </ul>
          </section>

          <section className="policy-section" aria-labelledby="contact-title">
            <h2 id="contact-title">Contact Us</h2>
            <p>
              If you have questions about this privacy policy or want to exercise your rights, 
              please contact us:
            </p>
            <div className="contact-info">
              <p><strong>Email:</strong> <a href="mailto:privacy@communicationmatters.org.uk">privacy@communicationmatters.org.uk</a></p>
              <p><strong>Address:</strong> Communication Matters, Leeds, UK</p>
              <p><strong>Data Protection Officer:</strong> Available upon request</p>
            </div>
          </section>

          <section className="policy-section" aria-labelledby="changes-title">
            <h2 id="changes-title">Policy Changes</h2>
            <p>
              We may update this privacy policy from time to time. We will notify you of any 
              significant changes by posting the new policy on this page and updating the 
              "Last Updated" date above.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;